import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { hsclLogo2 } from '../../assets/images'
import {
    IconBrandFacebook,
    IconBrandTwitter,
    IconUsersGroup,
    IconClock,
    IconChartBar,
    IconCreditCard,
    IconChevronLeft
} from '@tabler/icons-react'

export default function Register() {
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        setIsLoaded(true)
    }, [])

    return (
        <div className='flex h-svh overflow-hidden bg-white'>
            {/* Left Side: Brand & Features (40%) */}
            <div className='hidden lg:flex lg:w-[40%] flex-col bg-gradient-to-br from-[#011a4d] via-[#012E80] to-[#01245f] p-16 xl:p-24 text-white relative overflow-hidden'>
                {/* Mesh Glow Blobs */}
                <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-white/10 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#012E80]/30 rounded-full blur-[80px]"></div>

                {/* Logo top-left */}
                <div className={`mb-20 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                    <img src={hsclLogo2} alt='HSCL' className='h-12 w-auto object-contain opacity-90' />
                </div>

                <div className='flex-1 flex flex-col justify-center relative z-10'>
                    <h2 className={`text-4xl xl:text-5xl font-bold mb-16 tracking-tight transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Hi, Welcome back
                    </h2>

                    <div className='space-y-10'>
                        {[
                            { icon: IconUsersGroup, title: 'Comprehensive HR Management', desc: 'Streamline employee data, departments, and attendance tracking in one central hub.' },
                            { icon: IconClock, title: 'Leave & Time Tracking', desc: 'Effortlessly manage leave requests, balances, and detailed timesheets for your workforce.' },
                            { icon: IconChartBar, title: 'Performance & KPI Insights', desc: 'Monitor organizational growth with intuitive KPI dashboards and performance history tracking.' },
                            { icon: IconCreditCard, title: 'Financial & Accounts Control', desc: 'Stay on top of your bottom line with integrated management of payments, expenses, and invoices.' }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`flex items-start gap-6 group transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${100 + (idx * 150)}ms` }}
                            >
                                <div className='p-3.5 bg-white/10 rounded-2xl group-hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/5'>
                                    <item.icon size={26} className='text-white' />
                                </div>
                                <div className='pt-1'>
                                    <h3 className='text-lg xl:text-xl font-bold mb-1.5 group-hover:text-white transition-colors'>{item.title}</h3>
                                    <p className='text-white/70 text-sm leading-relaxed max-w-sm font-light'>
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Side: Register Form (60%) */}
            <div className='w-full lg:w-[60%] flex flex-col bg-white overflow-y-auto px-8 md:px-16 lg:px-24 py-12'>
                <div className={`flex-1 flex items-center justify-center py-20 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className='w-full max-w-[460px]'>
                        <div className='text-center mb-14'>
                            <h1 className='text-4xl font-black text-gray-900 mb-3 tracking-tight'>Create account</h1>
                            <p className="text-gray-500 font-light">Join the AgoraCore ERP Platform today</p>
                        </div>

                        <form className='flex flex-col gap-6'>
                            <div className='flex flex-col gap-2'>
                                <label className='text-sm font-semibold text-gray-700 ml-1'>Full Name</label>
                                <input
                                    type='text'
                                    placeholder='Enter your name'
                                    className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#6CBC50] focus:ring-2 focus:ring-[#6CBC50]/20 transition-all outline-none text-gray-800'
                                />
                            </div>

                            <div className='flex flex-col gap-2'>
                                <label className='text-sm font-semibold text-gray-700 ml-1'>Email Address</label>
                                <input
                                    type='email'
                                    placeholder='Enter your email'
                                    className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#6CBC50] focus:ring-2 focus:ring-[#6CBC50]/20 transition-all outline-none text-gray-800'
                                />
                            </div>

                            <div className='flex flex-col gap-2'>
                                <label className='text-sm font-semibold text-gray-700 ml-1'>Password</label>
                                <input
                                    type='password'
                                    placeholder='Create a password'
                                    className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#6CBC50] focus:ring-2 focus:ring-[#6CBC50]/20 transition-all outline-none text-gray-800'
                                />
                            </div>

                            <button
                                type='button'
                                className='mt-4 py-4 bg-[#6CBC50] hover:bg-[#5aa341] text-white text-lg font-bold rounded-xl transition-all duration-300 transform active:scale-[0.98] shadow-lg shadow-[#6CBC50]/20'
                            >
                                Register Now
                            </button>

                            <div className='text-center mt-2'>
                                <p className='text-sm text-gray-600'>
                                    Already have an account?{' '}
                                    <Link to="/auth-login" className='font-bold text-[#012E80] hover:underline'>
                                        Sign in
                                    </Link>
                                </p>
                            </div>

                            <div className='relative my-4'>
                                <div className='absolute inset-0 flex items-center'>
                                    <div className='w-full border-t border-gray-100'></div>
                                </div>
                                <div className='relative flex justify-center text-xs uppercase'>
                                    <span className='bg-white px-4 text-gray-400 font-medium tracking-widest'>Or register with</span>
                                </div>
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <button type="button" className='flex items-center justify-center gap-3 py-3.5 px-4 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors shadow-sm'>
                                    <IconBrandFacebook size={20} className='text-[#1877F2] fill-[#1877F2]' />
                                    <span className='text-sm font-semibold text-gray-700'>Facebook</span>
                                </button>
                                <button type="button" className='flex items-center justify-center gap-3 py-3.5 px-4 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors shadow-sm'>
                                    <IconBrandTwitter size={20} className='text-[#1DA1F2] fill-[#1DA1F2]' />
                                    <span className='text-sm font-semibold text-gray-700'>Twitter</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Footer Visibility Improved */}
                <div className={`mt-auto pt-10 flex justify-center transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className='flex items-center gap-3 opacity-70 hover:opacity-100 transition-all duration-500 py-4 px-8 rounded-full border border-gray-200 hover:border-[#012E80]/20 hover:bg-[#012E80]/5 group cursor-default'>
                        <span className='text-[10px] uppercase tracking-[0.3em] font-medium text-gray-500'>Powered by</span>
                        <img src={hsclLogo2} alt='HSCL' className='h-6 w-auto object-contain brightness-95 group-hover:brightness-100' />
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; transform: scale(1); }
                    50% { opacity: 0.2; transform: scale(1.1); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite ease-in-out;
                }
            `}</style>
        </div>
    )
}
